/**
 * DataNetwork — the sign-in backdrop: data nodes that drift, link to nearby
 * neighbours, and fire packets along those links.
 *
 * Ported from the `modern-erp-login` design. Two changes from the original:
 *
 *   Tokens are read from the container element, not from :root. The design's
 *   tokens live on the page's own scope here (signin.css) rather than the
 *   document root, so getComputedStyle(document.documentElement) would return
 *   empty strings and every colour would silently fall back to its hard-coded
 *   default — the animation would still run, in almost the right colours,
 *   which is the kind of wrong that never gets noticed.
 *
 *   The loop stops when the tab is hidden. This is the signed-out screen, so
 *   it is exactly the tab someone leaves open all afternoon; a
 *   requestAnimationFrame loop with per-frame shadow blur does not need to
 *   keep running behind other windows.
 */
import { useEffect, useRef } from 'react';

type Node = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** 0..1 phase offset, so the nodes don't breathe in unison. */
  pulse: number;
};

type Packet = {
  from: number;
  to: number;
  /** 0..1 progress along the edge. */
  t: number;
  speed: number;
};

type Rgb = { r: number; g: number; b: number };

function hexToRgb(hex: string): Rgb {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const int = Number.parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function readToken(el: Element, name: string, fallback: string): Rgb {
  const value = getComputedStyle(el).getPropertyValue(name).trim();
  return hexToRgb(/^#[0-9a-f]{3,8}$/i.test(value) ? value : fallback);
}

export function DataNetwork() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const navy = readToken(canvas, '--pacxa-navy', '#252754');
    const navyLight = readToken(canvas, '--pacxa-navy-light', '#363a73');
    const turquoise = readToken(canvas, '--pacxa-turquoise', '#5cbfb4');
    const blueLight = readToken(canvas, '--pacxa-blue-light', '#3b5199');

    const rgba = (c: Rgb, a: number) => `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;

    let width = 0;
    let height = 0;
    let nodes: Node[] = [];
    let packets: Packet[] = [];
    /** Two nodes within this many px are drawn as connected. */
    const LINK_DIST = 190;

    const seed = () => {
      // Node count scales with area, capped so a large monitor doesn't turn
      // the O(n²) link pass into a frame-rate problem.
      const target = Math.min(64, Math.max(28, Math.round((width * height) / 26000)));
      nodes = Array.from({ length: target }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: 1.5 + Math.random() * 2.5,
        pulse: Math.random(),
      }));
      packets = [];
    };

    const resize = () => {
      const parent = canvas.parentElement;
      width = parent?.clientWidth ?? window.innerWidth;
      height = parent?.clientHeight ?? window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const spawnPacket = () => {
      if (nodes.length < 2) return;
      const from = Math.floor(Math.random() * nodes.length);
      let best = -1;
      let bestDist = LINK_DIST;
      for (let j = 0; j < nodes.length; j++) {
        if (j === from) continue;
        const a = nodes[from]!;
        const b = nodes[j]!;
        const d = Math.hypot(b.x - a.x, b.y - a.y);
        // The random term keeps it from always picking the nearest neighbour,
        // which would make the same few edges light up over and over.
        if (d < bestDist && Math.random() > 0.4) {
          best = j;
          bestDist = d;
        }
      }
      if (best >= 0) {
        packets.push({
          from,
          to: best,
          t: 0,
          speed: 0.006 + Math.random() * 0.01,
        });
      }
    };

    let raf = 0;
    let lastSpawn = 0;

    const draw = (now: number) => {
      ctx.clearRect(0, 0, width, height);

      const bg = ctx.createLinearGradient(0, 0, width, height);
      bg.addColorStop(0, rgba(navy, 1));
      bg.addColorStop(0.55, rgba(navy, 1));
      bg.addColorStop(1, rgba(navyLight, 1));
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      for (const n of nodes) {
        if (reduceMotion) break;
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      }

      // Links between near neighbours, fading out with distance.
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]!;
          const b = nodes[j]!;
          const d = Math.hypot(b.x - a.x, b.y - a.y);
          if (d >= LINK_DIST) continue;
          ctx.strokeStyle = rgba(blueLight, (1 - d / LINK_DIST) * 0.28);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      packets = packets.filter((p) => p.t <= 1);
      for (const p of packets) {
        if (!reduceMotion) p.t += p.speed;
        const a = nodes[p.from];
        const b = nodes[p.to];
        if (!a || !b) continue;
        const px = a.x + (b.x - a.x) * p.t;
        const py = a.y + (b.y - a.y) * p.t;

        ctx.strokeStyle = rgba(turquoise, 0.55 * (1 - p.t));
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(px, py);
        ctx.stroke();

        ctx.shadowColor = rgba(turquoise, 0.9);
        ctx.shadowBlur = 10;
        ctx.fillStyle = rgba(turquoise, 1);
        ctx.beginPath();
        ctx.arc(px, py, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      for (const n of nodes) {
        const phase = reduceMotion ? 0.5 : 0.5 + 0.5 * Math.sin(now / 1400 + n.pulse * Math.PI * 2);
        ctx.fillStyle = rgba(turquoise, 0.25 + phase * 0.35);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + phase * 1.2, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = rgba(turquoise, 0.9);
        ctx.beginPath();
        ctx.arc(n.x, n.y, Math.max(1, n.r * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }

      if (!reduceMotion && now - lastSpawn > 260) {
        spawnPacket();
        lastSpawn = now;
      }

      raf = window.requestAnimationFrame(draw);
    };

    const start = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(draw);
    };

    const stop = () => {
      if (!raf) return;
      window.cancelAnimationFrame(raf);
      raf = 0;
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    resize();
    // Pre-seed a few so the backdrop looks alive on the first painted frame
    // rather than filling in over the following second.
    for (let i = 0; i < 8; i++) spawnPacket();
    start();

    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div className="pxsignin__backdrop" aria-hidden="true">
      <canvas ref={canvasRef} className="pxsignin__canvas" />
      <div className="pxsignin__scrim" />
    </div>
  );
}
